import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  USUARIOS_KEYS,
  EMPRESAS_PARA_SELECT_KEYS,
  listarUsuarios,
  listarEmpresasParaSelect,
  criarUsuario,
  atualizarUsuario,
  atribuirGestor,
  devolverColaborador,
  buscarMeuGestor,
  buscarMeusColaboradores,
  listarSalarios,
  criarSalario,
  atualizarSalario,
  excluirSalario,
  listarDocumentos,
  criarDocumento,
  deletarDocumento,
} from '../api';
import type { User, DocumentType } from '../../../shared/lib/api';

interface ListParams {
  companyId?: string;
  role?: string;
  limit?: number;
  offset?: number;
}

type CreateUsuario = Omit<User, 'id' | 'createdAt' | 'updatedAt'>;
type UpdateUsuario = { name: string; email: string; phone: string; companyId?: string };

export function useUsuarios(params?: ListParams) {
  return useQuery({
    queryKey: USUARIOS_KEYS.list(params),
    queryFn: () => listarUsuarios(params),
  });
}

export function useEmpresasParaSelect() {
  return useQuery({
    queryKey: EMPRESAS_PARA_SELECT_KEYS.all,
    queryFn: listarEmpresasParaSelect,
  });
}

export function useCriarUsuario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateUsuario) => criarUsuario(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: USUARIOS_KEYS.all }),
  });
}

export function useAtualizarUsuario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUsuario }) =>
      atualizarUsuario(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: USUARIOS_KEYS.all }),
  });
}

export const MEU_GESTOR_KEY = ['me', 'manager'] as const;
export const MEUS_COLABORADORES_KEY = ['me', 'collaborators'] as const;

export function useAtribuirGestor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ collaboratorId, managerId }: { collaboratorId: string; managerId: string }) =>
      atribuirGestor(collaboratorId, managerId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: USUARIOS_KEYS.all }),
  });
}

export function useDevolverColaborador() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ collaboratorId, motivo }: { collaboratorId: string; motivo: string }) =>
      devolverColaborador(collaboratorId, motivo),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: MEUS_COLABORADORES_KEY }),
  });
}

export function useMeuGestor() {
  return useQuery({
    queryKey: MEU_GESTOR_KEY,
    queryFn: buscarMeuGestor,
  });
}

export function useMeusColaboradores() {
  return useQuery({
    queryKey: MEUS_COLABORADORES_KEY,
    queryFn: buscarMeusColaboradores,
  });
}

export const DOCUMENTOS_KEY = (userId: string) => ['usuarios', userId, 'documentos'] as const;

export function useDocumentos(userId: string) {
  return useQuery({
    queryKey: DOCUMENTOS_KEY(userId),
    queryFn: () => listarDocumentos(userId),
    enabled: !!userId,
  });
}

export function useCriarDocumento(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { documentType: DocumentType; file: File }) =>
      criarDocumento(userId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: DOCUMENTOS_KEY(userId) }),
  });
}

export function useDeletarDocumento(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (docId: string) => deletarDocumento(userId, docId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: DOCUMENTOS_KEY(userId) }),
  });
}

export const SALARIOS_KEY = (collaboratorId: string) => ['usuarios', collaboratorId, 'salarios'] as const;

export function useSalarios(collaboratorId: string) {
  return useQuery({
    queryKey: SALARIOS_KEY(collaboratorId),
    queryFn: () => listarSalarios(collaboratorId),
    enabled: !!collaboratorId,
  });
}

/** Salário muda o valor que a liberação de NF mostra: invalida as duas coisas. */
function useSalarioMutation<TVars>(collaboratorId: string, fn: (vars: TVars) => Promise<unknown>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SALARIOS_KEY(collaboratorId) });
      queryClient.invalidateQueries({ queryKey: ['notas-fiscais', 'release-preview'] });
    },
  });
}

export const useCriarSalario = (collaboratorId: string) =>
  useSalarioMutation(collaboratorId, (payload: { amount: number; validFrom: string }) =>
    criarSalario(collaboratorId, payload),
  );

export const useAtualizarSalario = (collaboratorId: string) =>
  useSalarioMutation(collaboratorId, ({ id, ...payload }: { id: string; amount?: number; validFrom?: string }) =>
    atualizarSalario(collaboratorId, id, payload),
  );

export const useExcluirSalario = (collaboratorId: string) =>
  useSalarioMutation(collaboratorId, (id: string) => excluirSalario(collaboratorId, id));
